import type { Column } from "@shared/types/data-table"
import type { Subscription } from "@/features/subscriptions/types/subscriptions/subscription.types"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createTextColumn,
  createStatusColumn,
  createDateColumn
} from "./column-factories"

export const SUBSCRIPTION_COLUMNS: Record<string, Column<Subscription>> = {
  customer: createTextColumn<Subscription>("customer", "Customer", 200, "font-medium"),
  status: createStatusColumn<Subscription>("subscription"),
  plan: createTextColumn<Subscription>("plan", "Plan", 180),
  product: createTextColumn<Subscription>("product", "Product", 180),
  avgMonthly: createTextColumn<Subscription>("avgMonthly", "Avg. monthly", 150, "font-medium"),
  avgYearly: createTextColumn<Subscription>("avgYearly", "Avg. yearly", 150, "font-medium"),
  created: createDateColumn<Subscription>("created", "Created", 150, false),
}

// Status-specific column configuration
export const SUBSCRIPTION_STATUS_CONFIG = {
  all: ['customer', 'status', 'plan', 'product', 'avgMonthly', 'avgYearly', 'created'],
  active: ['customer', 'status', 'plan', 'product', 'avgMonthly', 'avgYearly', 'created'],
  inactive: ['customer', 'status', 'plan', 'product', 'avgMonthly', 'avgYearly', 'created'],
  scheduled: ['customer', 'status', 'plan', 'product', 'avgMonthly', 'avgYearly', 'created'],
  cancelled: ['customer', 'status', 'plan', 'product', 'avgMonthly', 'avgYearly', 'created'],
}

// Returns columns for specific status
export const getSubscriptionColumns = (status: string = 'all'): Column<Subscription>[] => {
  const keys = SUBSCRIPTION_STATUS_CONFIG[status as keyof typeof SUBSCRIPTION_STATUS_CONFIG] || SUBSCRIPTION_STATUS_CONFIG.all
  return keys.map(key => SUBSCRIPTION_COLUMNS[key]).filter(Boolean)
}

// Column management config for visibility/ordering
export const getSubscriptionColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(SUBSCRIPTION_STATUS_CONFIG[status as keyof typeof SUBSCRIPTION_STATUS_CONFIG] || SUBSCRIPTION_STATUS_CONFIG.all)
  return Object.entries(SUBSCRIPTION_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['customer', 'status'].includes(key),
    order: index
  }))
}
